/**
 * Auto-registration of entity access check listeners.
 *
 * For entities with `attachable`, registers `storage.checkEntityAccess`.
 * For entities with `taggeable`, registers `taxonomy.checkEntityAccess`.
 * Eliminates boilerplate in each module's init().
 */

import type { ModuleContext, ModuleManifest } from '@gzl10/nexus-sdk'

interface RegisteredModule extends ModuleManifest {
  _tablePrefix?: string
}

/**
 * Derive the short entity name from a table name and module name.
 * 'storage_files' with module 'storage' → 'files'
 * 'tags' with module 'taxonomy' → 'tags'
 */
export function deriveEntityName(table: string, moduleName: string): string {
  const prefix = `${moduleName}_`
  return table.startsWith(prefix) ? table.slice(prefix.length) : table
}

/**
 * Register access check listeners for all entities with attachable/taggeable.
 * Called once after all modules are initialized (services registered).
 */
export function registerEntityAccessChecks(ctx: ModuleContext, modules: RegisteredModule[]): void {
  for (const mod of modules) {
    for (const def of mod.definitions ?? []) {
      const d = def as Record<string, unknown>
      const table = d['table'] as string | undefined
      if (!table) continue

      if (d['expose'] === false) continue

      const caslSubject = (d['casl'] as Record<string, unknown> | undefined)?.['subject'] as string | undefined
      const attachable = d['attachable']
      const taggeable = d['taggeable']

      if (!attachable && !taggeable) continue

      // Resolve actual table name (plugins add prefix)
      const resolvedTable = mod._tablePrefix ? `${mod._tablePrefix}${table}` : table
      // Short name for manual-style entityType: storage.files
      const shortName = deriveEntityName(table, mod.name)
      const entityType = `${mod.name}.${shortName}`
      // Alt for runtime-style entityType: storage.storage_files
      const entityTypeAlt = shortName !== resolvedTable ? `${mod.name}.${resolvedTable}` : undefined

      if (attachable) {
        registerAccessListener(ctx, 'storage.checkEntityAccess', entityType, entityTypeAlt, resolvedTable, caslSubject, mod.name)
      }

      if (taggeable) {
        registerAccessListener(ctx, 'taxonomy.checkEntityAccess', entityType, entityTypeAlt, resolvedTable, caslSubject, mod.name)
      }
    }
  }
}

function registerAccessListener(
  ctx: ModuleContext,
  event: string,
  entityType: string,
  entityTypeAlt: string | undefined,
  serviceKey: string,
  caslSubject: string | undefined,
  _moduleName: string
): void {
  ctx.events.on(event, async (...args: unknown[]) => {
    const payload = args[0] as { entityType: string; entityId: string; userId?: string; ability?: any }

    if (payload.entityType !== entityType && (!entityTypeAlt || payload.entityType !== entityTypeAlt)) {
      return undefined
    }

    const service = ctx.services.getOptional<any>(serviceKey)
    if (!service?.findById) {
      ctx.core.logger.warn(`Auto access check: service '${serviceKey}' not found for ${entityType}`)
      return false
    }

    const record = await service.findById(payload.entityId)
    if (!record) return false

    if (payload.ability && caslSubject) {
      const { subject } = ctx.core.abilities
      return payload.ability.can('read', subject(caslSubject, record as Record<string, unknown>))
    }

    return true
  })

  ctx.core.logger.debug(`Auto access check: ${event} registered for ${entityType}`)
}
